// src/utils/positioning.ts

import type { Position } from '../types'

export interface PositionStyles {
  position: 'fixed'
  top?: string
  bottom?: string
  left?: string
  right?: string
  transform?: string
  zIndex: number
  pointerEvents: 'auto'
}

/**
 * Get inline styles for banner positioning
 * Uses fixed positioning with inline styles for maximum reliability
 */
export function getPositionStyles(position: Position, zIndex: number = 999998): PositionStyles {
  const baseStyles: PositionStyles = {
    position: 'fixed',
    zIndex,
    pointerEvents: 'auto',
  }

  const offset = {
    mobile: '16px',
    desktop: '24px',
  }

  switch (position) {
    case 'bottom-left':
      return {
        ...baseStyles,
        bottom: offset.mobile,
        left: offset.mobile,
      }

    case 'bottom-center':
      return {
        ...baseStyles,
        bottom: offset.mobile,
        left: '50%',
        transform: 'translateX(-50%)',
      }

    case 'bottom-right':
      return {
        ...baseStyles,
        bottom: offset.mobile,
        right: offset.mobile,
      }

    case 'top-left':
      return {
        ...baseStyles,
        top: offset.mobile,
        left: offset.mobile,
      }

    case 'top-center':
      return {
        ...baseStyles,
        top: offset.mobile,
        left: '50%',
        transform: 'translateX(-50%)',
      }

    case 'top-right':
      return {
        ...baseStyles,
        top: offset.mobile,
        right: offset.mobile,
      }

    default:
      return {
        ...baseStyles,
        bottom: offset.mobile,
        left: offset.mobile,
      }
  }
}

/**
 * Get styles for full-width minimal banner
 */
export function getMinimalPositionStyles(isTop: boolean, zIndex: number = 999998): PositionStyles {
  return {
    position: 'fixed',
    ...(isTop ? { top: '0' } : { bottom: '0' }),
    left: '0',
    right: '0',
    zIndex,
    pointerEvents: 'auto',
  }
}

/**
 * Get styles for sidebar banner
 */
export function getSidebarPositionStyles(isLeft: boolean, zIndex: number = 999998): PositionStyles {
  return {
    position: 'fixed',
    top: '0',
    bottom: '0',
    ...(isLeft ? { left: '0' } : { right: '0' }),
    zIndex,
    pointerEvents: 'auto',
  }
}

/**
 * Get responsive max width based on screen size
 */
export function getResponsiveMaxWidth(size?: string): string {
  if (!size) return '448px' // md default

  const sizes: Record<string, string> = {
    sm: '384px',
    md: '448px',
    lg: '512px',
    xl: '576px',
  }

  return sizes[size] || size
}

/**
 * Get container styles with isolation and max-width
 */
export interface ContainerStyles {
  maxWidth: string
  width: string
  boxSizing: 'border-box'
  isolation: 'isolate'
}

export function getContainerStyles(maxWidth?: string): ContainerStyles {
  return {
    maxWidth: getResponsiveMaxWidth(maxWidth),
    width: 'calc(100% - 32px)', // 16px padding on each side mobile
    boxSizing: 'border-box',
    isolation: 'isolate',
  }
}

/**
 * Combine all positioning styles
 */
export function getCombinedStyles(
  position: Position,
  maxWidth?: string,
  customStyles?: React.CSSProperties
): React.CSSProperties {
  const positionStyles = getPositionStyles(position)
  const containerStyles = getContainerStyles(maxWidth)

  return {
    ...positionStyles,
    ...containerStyles,
    ...customStyles,
  }
}
